'use client';

import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useCommerceSource, useBlockPart, type Facet } from '@akademi/site-builder-runtime';
import { Checkbox, Input, Button } from '@akademi/ui';
import { AdapterPlaceholder, BlockRoot } from '@akademi/site-builder-blocks';
import {
  readProductQuery,
  patchProductParams,
  toggleFacetValue,
  isFacetValueSelected,
  hasActiveProductFilters,
  clearProductParams,
  type ProductQueryScope,
} from './productQuery';

/**
 * Renders {@code Facet[]} and reports the visitor's selection — by writing
 * it into the page URL, the same "no shared React state" pattern
 * {@code CourseGrid} + {@code CategoryTree} already use in this package.
 * Drop this anywhere on a page that also carries a {@code ProductGrid} with
 * the same {@code categorySlugPath} / {@code brandSlug} / {@code
 * productType} scope and the two stay in sync with zero prop wiring — see
 * {@code productQuery.ts} for the shared URL scheme.
 *
 * <p>Facets themselves come from {@code CommerceSource.listProducts()} —
 * there is no separate "facets only" port on the contract, so this block
 * calls {@code listProducts} with the current filters and a minimal page
 * size, then reads only {@code .facets} off the result (discards
 * {@code .items}). Two blocks on one page therefore each make their own
 * call; that's the accepted cost of two fully independent, URL-synced
 * blocks rather than one having to know about the other.
 *
 * <h3>Accessibility</h3>
 * Each facet group is a native {@code <details>/<summary>} disclosure —
 * keyboard-operable and announced as expanded/collapsed by every screen
 * reader with no bespoke ARIA. Facet values are real
 * {@code <input type="checkbox">} + {@code <label>} pairs (from
 * {@code @akademi/ui}'s {@code Checkbox}, which ships a
 * {@code focus-visible} ring already). Min/max price use a real
 * {@code <form>} with a submit button — Enter applies the range without
 * requiring a mouse.
 */
export interface FacetPanelProps {
  title?: string;
  categorySlugPath?: string;
  brandSlug?: string;
  productType?: string;
  featured?: boolean;
  showPriceRange?: boolean;
  showInStockOnly?: boolean;
  /** Facet groups start expanded (default) or collapsed. */
  defaultExpanded?: boolean;
}

export function FacetPanel({
  title,
  categorySlugPath,
  brandSlug,
  productType,
  featured,
  showPriceRange = true,
  showInStockOnly = true,
  defaultExpanded = true,
}: FacetPanelProps) {
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const scope: ProductQueryScope = { categorySlugPath, brandSlug, productType, featured };
  const query = readProductQuery(searchParams, scope);
  const queryKey = JSON.stringify(query);

  const [facets, setFacets] = useState<Facet[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    if (!commerce) return;
    let cancelled = false;
    setStatus('loading');
    // size: 1 — this block only wants `.facets`, not `.items`; keeping the
    // page tiny avoids paying for a full items payload twice per page load.
    commerce
      .listProducts({ ...query, page: 1, size: 1 })
      .then((result) => {
        if (cancelled) return;
        setFacets(result.facets ?? []);
        setStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setFacets(null);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [commerce, queryKey]);

  const [minPrice, setMinPrice] = useState(query.minPrice ?? '');
  const [maxPrice, setMaxPrice] = useState(query.maxPrice ?? '');
  useEffect(() => {
    setMinPrice(query.minPrice ?? '');
    setMaxPrice(query.maxPrice ?? '');
  }, [query.minPrice, query.maxPrice]);

  function replaceParams(params: URLSearchParams) {
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }

  function onToggleFacet(code: string, value: string) {
    replaceParams(toggleFacetValue(searchParams, code, value));
  }

  function onSubmitPriceRange(e: FormEvent) {
    e.preventDefault();
    replaceParams(
      patchProductParams(searchParams, {
        minPrice: minPrice.trim() || undefined,
        maxPrice: maxPrice.trim() || undefined,
      }),
    );
  }

  function onToggleInStock(checked: boolean) {
    replaceParams(patchProductParams(searchParams, { inStock: checked ? 'true' : undefined }));
  }

  function onClearAll() {
    setMinPrice('');
    setMaxPrice('');
    replaceParams(clearProductParams(searchParams));
  }

  if (!commerce) {
    return (
      <BlockRoot as="nav" aria-label={title ?? t('commerce.facets.label')} style={panelStyle}>
        <AdapterPlaceholder
          blockType="FacetPanel"
          adapterName="CommerceSource"
          reason={t('commerce.adapter.reason')}
        />
      </BlockRoot>
    );
  }

  const inStockOnly = searchParams?.get('inStock') === 'true';
  const showClear = hasActiveProductFilters(searchParams);
  const hasFacetGroups = (facets?.length ?? 0) > 0;

  return (
    <BlockRoot
      as="nav"
      aria-label={title ?? t('commerce.facets.label')}
      className="sb-facet-panel"
      style={panelStyle}
    >
      <style dangerouslySetInnerHTML={{ __html: FACET_PANEL_CSS }} />
      <div style={headerRowStyle}>
        <h2 style={headingStyle}>{title ?? t('commerce.facets.label')}</h2>
        {showClear && (
          <button type="button" onClick={onClearAll} style={clearButtonStyle} {...part('clear')}>
            {t('commerce.facets.clearAll')}
          </button>
        )}
      </div>

      {status === 'error' && <p style={mutedStyle}>{t('commerce.facets.error')}</p>}

      {showInStockOnly && (
        <div style={inlineFieldStyle}>
          <Checkbox
            id="sb-facet-in-stock"
            checked={inStockOnly}
            onCheckedChange={(v) => onToggleInStock(v === true)}
          />
          <label htmlFor="sb-facet-in-stock" style={checkboxLabelStyle}>
            {t('commerce.facets.inStockOnly')}
          </label>
        </div>
      )}

      {showPriceRange && (
        <form
          onSubmit={onSubmitPriceRange}
          style={priceFormStyle}
          aria-label={t('commerce.facets.priceRange')}
        >
          <span style={groupLegendStyle}>{t('commerce.facets.priceRange')}</span>
          <div style={priceInputsRowStyle}>
            <label style={visuallyHiddenStyle} htmlFor="sb-facet-min-price">
              {t('commerce.facets.minPrice')}
            </label>
            <Input
              id="sb-facet-min-price"
              type="text"
              inputMode="decimal"
              placeholder={t('commerce.facets.minPrice')}
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
            />
            <label style={visuallyHiddenStyle} htmlFor="sb-facet-max-price">
              {t('commerce.facets.maxPrice')}
            </label>
            <Input
              id="sb-facet-max-price"
              type="text"
              inputMode="decimal"
              placeholder={t('commerce.facets.maxPrice')}
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
            />
            <Button type="submit" variant="outline" size="sm">
              {t('commerce.facets.apply')}
            </Button>
          </div>
        </form>
      )}

      {status === 'loading' && !hasFacetGroups && (
        <div aria-hidden style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="theme-skeleton" style={{ height: 16, borderRadius: 4 }} />
          ))}
        </div>
      )}

      {facets?.map((facet) => (
        <details key={facet.code} open={defaultExpanded} style={groupStyle} {...part('group')}>
          <summary style={summaryStyle}>{facet.label}</summary>
          <div style={valueListStyle}>
            {facet.values.map((v) => {
              const checked = isFacetValueSelected(searchParams, facet.code, v.value);
              const inputId = `sb-facet-${facet.code}-${v.value}`;
              return (
                <div key={v.value} style={inlineFieldStyle}>
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    onCheckedChange={() => onToggleFacet(facet.code, v.value)}
                  />
                  <label htmlFor={inputId} style={checkboxLabelStyle}>
                    {v.label}
                    {typeof v.count === 'number' && <span style={countStyle}> ({v.count})</span>}
                  </label>
                </div>
              );
            })}
          </div>
        </details>
      ))}

      {status === 'loaded' && !hasFacetGroups && !showPriceRange && !showInStockOnly && (
        <p style={mutedStyle}>{t('commerce.facets.none')}</p>
      )}
    </BlockRoot>
  );
}

// A visible focus ring on every interactive element here comes from the
// underlying <details>/<summary>/<button>/<input> browser defaults or the
// @akademi/ui Checkbox's own focus-visible ring — nothing in this block
// suppresses outline, so this stylesheet only adds layout + hover affordance.
const FACET_PANEL_CSS = `
  .sb-facet-panel summary::-webkit-details-marker { display: none; }
`;

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 'var(--space-4, 16px)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-card, #fff)',
};

const headerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
};

const headingStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-md, 16px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #18181b)',
};

const clearButtonStyle: CSSProperties = {
  background: 'none',
  border: 0,
  padding: 0,
  color: 'var(--color-brand-600, #287f96)',
  fontSize: 'var(--font-size-sm, 13px)',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const groupStyle: CSSProperties = {
  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
  padding: '10px 0',
};

const summaryStyle: CSSProperties = {
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #18181b)',
  listStyle: 'none',
};

const valueListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 10,
};

const inlineFieldStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 0',
};

const checkboxLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-primary, #18181b)',
  cursor: 'pointer',
};

const countStyle: CSSProperties = {
  color: 'var(--color-text-tertiary, #71717a)',
};

const priceFormStyle: CSSProperties = {
  borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
  padding: '10px 0',
};

const groupLegendStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #18181b)',
  marginBottom: 8,
};

const priceInputsRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const visuallyHiddenStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const mutedStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 13px)',
  color: 'var(--color-text-tertiary, #71717a)',
};
